/**
 * Limits and validation for the multi-turn fields of an AI `entity_invoke`:
 * `history` (prior conversation turns) and `parameters.reasoning`.
 *
 * ONE implementation, deliberately placed in the SDK. It is dependency-free, so
 * it can run in three places that must never disagree:
 *   - the platform bridges, which reject a malformed call with `INVALID_PARAMS`;
 *   - the dev harness's MOCK mode, which must reject the same calls offline
 *     (the harness is published to npm and may only depend on this SDK);
 *   - plugins, which use `fitHistoryToLimits` to trim a conversation BEFORE
 *     sending it rather than finding out from a rejected call.
 */
import type { EntityInvocationParams, EntityInvocationTurn, ReasoningEffort } from './types';

/**
 * Bounds on `EntityInvocationParams.history`, in characters. They are the
 * validator's rules, not a promise that a call fits: the request must ALSO fit
 * the bridge's byte limit (`ENTITY_INVOCATION_REQUEST_LIMITS`), and a character
 * can serialize to up to 4 bytes (CJK 3, emoji 4, a newline or quote 2). Trim
 * with `fitHistoryToLimits(turns, { request })`, which applies both.
 */
export const ENTITY_INVOCATION_HISTORY_LIMITS = {
  maxTurns: 40,
  maxTurnChars: 100_000,
  maxTotalChars: 150_000,
} as const;

/**
 * The platform bridge's request-size ceilings, in bytes of the serialized
 * (UTF-8 JSON) request. A text-only `entity_invoke` travels on the 256 KB
 * route; one carrying images on the 1.5 MB vision route. Mirrors the server's
 * `PLUGIN_BRIDGE_BODY_LIMIT_BYTES` / `PLUGIN_BRIDGE_VISION_BODY_LIMIT_BYTES`
 * (pinned by `tests/architecture/invocation-params-parity.test.ts`).
 * `envelopeBytes` is kept free for what the host wraps around the payload.
 */
export const ENTITY_INVOCATION_REQUEST_LIMITS = {
  textBytes: 256 * 1024,
  visionBytes: 1_536 * 1024,
  envelopeBytes: 1024,
} as const;

/** Bytes `value` occupies as UTF-8 JSON — what the bridge body limit counts. */
function serializedBytes(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).length;
}

function carriesImages(params: { images?: unknown }): boolean {
  return Array.isArray(params.images) && params.images.length > 0;
}

/**
 * The most bytes an `entity_invoke` payload may serialize to: the limit of the
 * route it will travel on, less the host's envelope.
 */
export function invocationPayloadByteLimit(params: { images?: unknown }): number {
  const { textBytes, visionBytes, envelopeBytes } = ENTITY_INVOCATION_REQUEST_LIMITS;
  return (carriesImages(params) ? visionBytes : textBytes) - envelopeBytes;
}

/**
 * Whether an `entity_invoke` payload fits the bridge's request limit. The SDK
 * checks this before sending, so an oversized call fails with
 * `REQUEST_TOO_LARGE` instead of an opaque HTTP 413 from the server.
 */
export function checkInvocationRequestSize(
  payload: Record<string, unknown>,
): InvocationParamCheck<number> {
  const bytes = serializedBytes(payload);
  const limit = invocationPayloadByteLimit(payload);
  if (bytes > limit) {
    return {
      ok: false,
      message:
        `The request is ${bytes} bytes; ${carriesImages(payload) ? 'a call with images' : 'a call without images'} ` +
        `may be at most ${limit}. Send less history (fitHistoryToLimits with { request }) or a shorter input.`,
    };
  }
  return { ok: true, value: bytes };
}

export const REASONING_EFFORTS: readonly ReasoningEffort[] = ['off', 'low', 'medium', 'high'];

export type InvocationParamCheck<T> =
  | { ok: true; value: T | undefined }
  | { ok: false; message: string };

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Validate a `history` value. `undefined` is valid (no history). Roles are
 * `user` / `assistant` only, so a caller can never inject a second system
 * message; unknown turn fields are rejected, not dropped.
 */
export function checkInvocationHistory(
  value: unknown,
): InvocationParamCheck<EntityInvocationTurn[]> {
  if (value === undefined) return { ok: true, value: undefined };
  const { maxTurns, maxTurnChars, maxTotalChars } = ENTITY_INVOCATION_HISTORY_LIMITS;
  if (!Array.isArray(value)) return { ok: false, message: 'history: must be an array' };
  if (value.length > maxTurns) {
    return { ok: false, message: `history: at most ${maxTurns} turns` };
  }
  let total = 0;
  for (let i = 0; i < value.length; i++) {
    const turn: unknown = value[i];
    if (!isPlainObject(turn)) return { ok: false, message: `history.${i}: must be an object` };
    const extra = Object.keys(turn).filter((k) => k !== 'role' && k !== 'content');
    if (extra.length > 0) {
      return { ok: false, message: `history.${i}: unknown field "${extra[0]}"` };
    }
    if (turn.role !== 'user' && turn.role !== 'assistant') {
      return { ok: false, message: `history.${i}.role: must be "user" or "assistant"` };
    }
    if (typeof turn.content !== 'string') {
      return { ok: false, message: `history.${i}.content: must be a string` };
    }
    if (turn.content.length > maxTurnChars) {
      return { ok: false, message: `history.${i}.content: at most ${maxTurnChars} characters` };
    }
    total += turn.content.length;
  }
  if (total > maxTotalChars) {
    return { ok: false, message: `history: at most ${maxTotalChars} characters in total` };
  }
  return { ok: true, value: value as EntityInvocationTurn[] };
}

/**
 * Validate `parameters.reasoning`. `undefined` is valid (no reasoning request);
 * `includeThinking` defaults to `false`.
 */
export function checkReasoningParameter(
  value: unknown,
): InvocationParamCheck<{ effort: ReasoningEffort; includeThinking: boolean }> {
  if (value === undefined) return { ok: true, value: undefined };
  if (!isPlainObject(value))
    return { ok: false, message: 'parameters.reasoning: must be an object' };
  const extra = Object.keys(value).filter((k) => k !== 'effort' && k !== 'includeThinking');
  if (extra.length > 0) {
    return { ok: false, message: `parameters.reasoning: unknown field "${extra[0]}"` };
  }
  if (!REASONING_EFFORTS.includes(value.effort as ReasoningEffort)) {
    return {
      ok: false,
      message: `parameters.reasoning.effort: must be one of ${REASONING_EFFORTS.join(', ')}`,
    };
  }
  if (value.includeThinking !== undefined && typeof value.includeThinking !== 'boolean') {
    return { ok: false, message: 'parameters.reasoning.includeThinking: must be a boolean' };
  }
  return {
    ok: true,
    value: {
      effort: value.effort as ReasoningEffort,
      includeThinking: value.includeThinking === true,
    },
  };
}

/**
 * The newest suffix of `turns` that fits: the character caps in
 * `ENTITY_INVOCATION_HISTORY_LIMITS` AND the bridge's byte limit, optionally
 * capped at `maxTurns` (e.g. a "last 20 messages" window).
 *
 * Pass the rest of the call as `request` (input, systemPrompt, images,
 * parameters — everything but `history`) so the byte budget is what that call
 * leaves over on the route it will take. Without it the budget assumes the
 * history is all there is, which a real call can still exceed.
 *
 * Older turns are dropped first; a single turn longer than the per-turn cap
 * keeps its most recent text. Every turn sent is billed as input on every call,
 * so a smaller window is also cheaper.
 */
export function fitHistoryToLimits(
  turns: readonly EntityInvocationTurn[],
  options: { maxTurns?: number; request?: Omit<EntityInvocationParams, 'history'> } = {},
): EntityInvocationTurn[] {
  const limits = ENTITY_INVOCATION_HISTORY_LIMITS;
  const maxTurns = Math.min(options.maxTurns ?? limits.maxTurns, limits.maxTurns);
  const request = (options.request ?? {}) as Record<string, unknown>;
  // What the call leaves for history: its route's limit less everything else
  // it carries, measured with an empty history in place (the SDK adds `stream`).
  let bytesLeft =
    invocationPayloadByteLimit(request) -
    serializedBytes({ ...request, stream: true, history: [] });
  const kept: EntityInvocationTurn[] = [];
  let total = 0;
  for (let i = turns.length - 1; i >= 0 && kept.length < maxTurns; i--) {
    const turn = turns[i];
    const content =
      turn.content.length > limits.maxTurnChars
        ? turn.content.slice(turn.content.length - limits.maxTurnChars)
        : turn.content;
    if (total + content.length > limits.maxTotalChars) break;
    // The turn as it travels, plus the comma that separates it from the next.
    const bytes = serializedBytes({ role: turn.role, content }) + (kept.length > 0 ? 1 : 0);
    if (bytes > bytesLeft) break;
    bytesLeft -= bytes;
    total += content.length;
    kept.push({ role: turn.role, content });
  }
  return kept.reverse();
}
